import { supabase } from '@/integrations/supabase/client';

// Fallback logo URL (current uploaded logo)
const FALLBACK_LOGO_URL = '/favicon.png';

// Get logo URL from settings
export async function getLogoUrl(): Promise<string> {
  const { data, error } = await supabase
    .from('site_settings')
    .select('value')
    .eq('key', 'logo_url')
    .single();

  if (error || !data?.value) return FALLBACK_LOGO_URL;
  return data.value;
}

const LOGO_CACHE_KEY = 'forja:logo:v1';

// Upload logo and update settings (with compression)
export async function uploadLogo(file: File): Promise<string> {
  // Import and compress image
  const { prepareImageForUpload } = await import('@/lib/imageUtils');
  const { blob, extension, mimeType } = await prepareImageForUpload(file, 'siteLogo');
  
  const fileName = `logo-${Date.now()}.${extension}`;
  const filePath = `logos/${fileName}`;

  // Upload file to storage
  const { error: uploadError } = await supabase.storage
    .from('branding')
    .upload(filePath, blob, { 
      upsert: true,
      contentType: mimeType
    });

  if (uploadError) throw uploadError;

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from('branding')
    .getPublicUrl(filePath);

  // Update settings
  const { error: updateError } = await supabase
    .from('site_settings')
    .update({ value: publicUrl, updated_at: new Date().toISOString() })
    .eq('key', 'logo_url');

  if (updateError) throw updateError;

  // Invalidate local cache
  localStorage.removeItem(LOGO_CACHE_KEY);

  return publicUrl;
}

// Reset logo to default (null)
export async function resetLogo(): Promise<void> {
  const { error } = await supabase
    .from('site_settings')
    .update({ value: null, updated_at: new Date().toISOString() })
    .eq('key', 'logo_url');

  if (error) throw error;

  // Invalidate local cache
  localStorage.removeItem(LOGO_CACHE_KEY);
}

// Fetch admin users with profiles, roles, and last sign in activity
export async function fetchAdminUsers() {
  // Use RPC that joins profiles with auth.users for last_sign_in_at
  // Note: Supabase has a default limit of 1000 rows, we need to fetch in batches
  const allUsers: { user_id: string; email: string | null; display_name: string | null; avatar_url: string | null; created_at: string; last_sign_in_at: string | null }[] = [];
  const batchSize = 1000;
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .rpc('get_admin_users_with_activity')
      .range(offset, offset + batchSize - 1);

    if (error) throw error;

    if (data && data.length > 0) {
      allUsers.push(...data);
      offset += batchSize;
      hasMore = data.length === batchSize;
    } else {
      hasMore = false;
    }
  }

  // Fetch all roles for these users (also in batches if needed)
  const userIds = allUsers.map((p) => p.user_id);
  const allRoles: { user_id: string; role: string }[] = [];
  
  // Batch user_ids to avoid query size limits
  const userIdBatches = [];
  for (let i = 0; i < userIds.length; i += 500) {
    userIdBatches.push(userIds.slice(i, i + 500));
  }

  for (const batch of userIdBatches) {
    const { data: rolesData } = await supabase
      .from('user_roles')
      .select('user_id, role')
      .in('user_id', batch);
    
    if (rolesData) {
      allRoles.push(...rolesData);
    }
  }

  // Merge profiles with roles
  return allUsers.map((profile) => ({
    ...profile,
    roles: allRoles.filter(r => r.user_id === profile.user_id).map(r => r.role) || []
  }));
}

// Update user role (seller roles removed - now uses seller_status on profiles)
export type AppRole = 'admin' | 'moderator' | 'partner' | 'staff' | 'student' | 'user' | 'visitor';

export async function updateUserRole(
  userId: string, 
  role: AppRole, 
  action: 'add' | 'remove'
): Promise<void> {
  if (action === 'add') {
    const { error } = await supabase
      .from('user_roles')
      .insert({ user_id: userId, role: role });
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId)
      .eq('role', role);
    if (error) throw error;
  }
}

// Get real metrics using RPC (single query instead of 4)
export async function fetchTerminalMetrics() {
  const [metricsResult, pwaCount] = await Promise.all([
    supabase.rpc('get_terminal_metrics'),
    supabase.from('pwa_installs' as any).select('*', { count: 'exact', head: true }),
  ]);
  
  if (metricsResult.error) {
    console.error('Error fetching terminal metrics:', metricsResult.error);
    throw metricsResult.error;
  }

  const metrics = metricsResult.data as { 
    members: number; 
    activeListings: number; 
    negotiations: number; 
    completedNegotiationsMonth: number;
    volume: number;
    students: number;
    visitors: number;
    staff: number;
    admins: number;
    partners: number;
    verifiedSellers: number;
    tiktok?: {
      monthlyTotal: number;
      monthlyShop: number;
      monthlyLiveShop: number;
      monthlyReward: number;
      monthlyAdditional: number;
      activeAccounts: number;
      recentVideos: number;
    };
    dailyActiveAvg?: number;
    dailyActiveToday?: number;
    dailyActiveYesterday?: number;
  } | null;

  return {
    members: metrics?.members ?? 0,
    activeListings: metrics?.activeListings ?? 0,
    negotiations: metrics?.negotiations ?? 0,
    completedNegotiationsMonth: metrics?.completedNegotiationsMonth ?? 0,
    volume: metrics?.volume ?? 0,
    students: metrics?.students ?? 0,
    visitors: metrics?.visitors ?? 0,
    staff: metrics?.staff ?? 0,
    admins: metrics?.admins ?? 0,
    partners: metrics?.partners ?? 0,
    verifiedSellers: metrics?.verifiedSellers ?? 0,
    tiktok: {
      monthlyTotal: metrics?.tiktok?.monthlyTotal ?? 0,
      monthlyShop: metrics?.tiktok?.monthlyShop ?? 0,
      monthlyLiveShop: metrics?.tiktok?.monthlyLiveShop ?? 0,
      monthlyReward: metrics?.tiktok?.monthlyReward ?? 0,
      monthlyAdditional: metrics?.tiktok?.monthlyAdditional ?? 0,
      activeAccounts: metrics?.tiktok?.activeAccounts ?? 0,
      recentVideos: metrics?.tiktok?.recentVideos ?? 0,
    },
    dailyActiveYesterday: metrics?.dailyActiveYesterday ?? 0,
    dailyActiveToday: metrics?.dailyActiveToday ?? 0,
    dailyActiveAvg: metrics?.dailyActiveAvg ?? 0,
    pwaInstalls: pwaCount.count ?? 0,
  };
}
